import {
  Button,
  CircularProgress,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Skeleton,
} from '@mui/material'

import { useTrayDetails } from './TrayDetails.logic'

import './TrayDetails.scss'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ArrowBack, Visibility, Delete } from '@mui/icons-material'
import { LoadingButton } from 'components/mui'
import { DataTable } from 'components/molecules/DataTable/DataTable'
import ApproveRejectTray from 'components/molecules/ApproveRejectTray/ApproveRejectTray'
import dayjs from 'lib/dayjs'
import useTrayManagementLogic from '../TrayManagement/TrayManagement.logic'
import { StickerPrintContainer } from 'components/organisms/TraySticker/TraySticker'
import { formatToTitleCase } from 'lib/utils/formatToTitleCase'
import { SubItem } from 'common/types'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'
import useDTMS3Image from 'lib/utils/useDTMS3Image'
import { getNavigationSource, navigateBack } from 'lib/utils/navigationUtils'

// Create a reusable SubTrayImage component
const SubTrayImage = ({ trayImg }: { trayImg: string }) => {
  const { imageUrl: signedImageUrl, isLoading } = useDTMS3Image({
    url: trayImg,
  })

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height="120px"
        animation="wave"
        sx={{ borderRadius: '8px' }}
      />
    )
  }

  return (
    <img
      src={signedImageUrl}
      alt="Sub tray"
      style={{
        width: '100%',
        height: '120px',
        borderRadius: '8px',
        objectFit: 'cover',
      }}
    />
  )
}

const TrayDetails = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const {
    loading,
    error,
    trayItem,
    deleting,
    handleDeleteTray,
    subTraysTableBodyCells,
    subTraysTableHeaderCells,
    isApproveRejectTrayOpen,
    handleApproveRejectTrayClick,
    isApproving,
    isRejecting,
    handleUpdateTrayApprovalStatus,
    isSPD,
    trayDetails,
    handleViewTrayFiles,
    notAssignedButSelectedSurgeon,
    handlePrintSticker,
    trayId,
    handleSubTrayClick,
    handleDeleteSubTray,
    handleLoadTray,
  } = useTrayDetails()
  const { getTrayStatusDisplay, setLastViewedTrayId } = useTrayManagementLogic()

  // Get navigation source from URL parameters
  const navigationSource = getNavigationSource(searchParams)

  const handleBackClick = () => {
    if (trayId) {
      setLastViewedTrayId(trayId)
    }
    // Use navigation utility to handle back navigation
    navigateBack(navigate, navigationSource)
  }

  const renderMobileSubTrayCard = (subTray: SubItem) => {
    const subTrayDetails = extractSubTrayJSONData(subTray)
    const isReadyToFill = trayDetails?.status === 'readyToFill' && isSPD
    const isFacilityTray = trayDetails?.trayCategory === 'facility'
    const canDeleteSubTray = isSPD && isFacilityTray

    if (!subTrayDetails) return null

    return (
      <Card key={subTray.id} className="mobile-sub-tray-card">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <SubTrayImage trayImg={subTrayDetails.trayImg} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  ID: {subTray.id}
                </Typography>
                <Typography variant="body2">
                  Type: {formatToTitleCase(subTrayDetails?.trayType || '')}
                </Typography>
                <Typography variant="body2">
                  Par Level: {subTrayDetails.parLevel}
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => handleSubTrayClick(subTray.id)}
                    fullWidth
                  >
                    View Details
                  </Button>

                  {isReadyToFill && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleLoadTray(trayItem)}
                      fullWidth
                    >
                      Load Tray
                    </Button>
                  )}

                  {canDeleteSubTray && (
                    <LoadingButton
                      className="delete-sub-tray-button"
                      loading={deleting}
                      disabled={deleting}
                      mode="delete"
                      onClick={() => handleDeleteSubTray(subTray.id)}
                      fullWidth
                    >
                      <Delete sx={{ mr: 1 }} /> Delete
                    </LoadingButton>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="tray-details">
      <div data-testid="header" className="header contained-desktop">
        <div>
          <Button
            variant="text"
            className="back_button"
            onClick={handleBackClick}
          >
            <ArrowBack sx={{ mr: 0.5 }} /> BACK
          </Button>
        </div>

        <div className="header_actions">
          <Button
            variant="text"
            sx={{ width: 'auto' }}
            onClick={handlePrintSticker}
          >
            Print Sticker
          </Button>

          {isSPD && trayDetails?.trayCategory !== 'facility' && (
            <Button
              variant="outlined"
              sx={{ width: 'auto' }}
              onClick={handleApproveRejectTrayClick}
            >
              Approve / Reject Tray
            </Button>
          )}

          <LoadingButton
            className="delete"
            dataTestId="delete-tray-button"
            loading={deleting}
            disabled={deleting}
            sx={{ width: 'auto' }}
            mode="delete"
            onClick={handleDeleteTray}
          >
            Delete Tray
          </LoadingButton>
        </div>
      </div>
      <div className="contained-desktop">
        <Typography variant="h1">Tray Details</Typography>

        <ApproveRejectTray
          open={isApproveRejectTrayOpen}
          onClose={handleApproveRejectTrayClick}
          isApproving={isApproving}
          isRejecting={isRejecting}
          handleApproval={() => handleUpdateTrayApprovalStatus('approved')}
          handleRejection={(rejectionReason) =>
            handleUpdateTrayApprovalStatus('rejected', rejectionReason)
          }
        />

        <div className="content">
          {loading && !error && <CircularProgress />}

          {error && !loading && (
            <Typography className="error" variant="body1">
              {error.message}
            </Typography>
          )}

          {!error && !loading && trayItem && (
            <>
              <div className="tray-details_box">
                <div className="tray-details_details">
                  <Typography className="tray-details_detail">
                    Tray ID: <strong>{trayItem?.barcode}</strong>
                  </Typography>
                  <Typography className="tray-details_detail">
                    Description: <strong>{trayItem?.description}</strong>
                  </Typography>
                  {notAssignedButSelectedSurgeon && (
                    <Typography className="tray-details_detail">
                      Selected Surgeon:{' '}
                      <strong>{trayDetails?.selectedSurgeon}</strong>
                    </Typography>
                  )}
                  <Box className="tray-details_row">
                    <Typography className="tray-details_detail">
                      Status:{' '}
                      <strong>
                        {getTrayStatusDisplay(trayDetails?.status)}
                      </strong>
                    </Typography>
                  </Box>
                </div>

                <div className="tray-details_right">
                  <LoadingButton
                    variant="outlined"
                    size={isMobile ? 'medium' : 'small'}
                    onClick={handleViewTrayFiles}
                    fullWidth={isMobile}
                  >
                    {trayDetails?.trayCategory !== 'facility' && 'IFU Files +'}{' '}
                    Inventory Sheets
                  </LoadingButton>
                  <div className="tray-details_updated">
                    <Typography variant="body2" className="tray-details_detail">
                      Last updated:{' '}
                      <strong>
                        {trayItem?.statusDetails?.lastUpdated
                          ? dayjs(trayItem?.statusDetails?.lastUpdated).format(
                              'MM/DD/YYYY hh:mm A'
                            )
                          : 'N/A'}
                      </strong>
                    </Typography>
                    <Typography variant="body2" className="tray-details_detail">
                      Last updated by:{' '}
                      <strong>
                        {trayItem?.statusDetails?.lastUpdatedBy
                          ? trayItem?.statusDetails?.lastUpdatedBy
                          : 'N/A'}
                      </strong>
                    </Typography>
                  </div>
                </div>
              </div>

              <div className="sub-trays-container">
                {isMobile ? (
                  <Box display="flex" flexDirection="column" gap={2}>
                    {trayItem.subItems.map((subTray: SubItem) =>
                      renderMobileSubTrayCard(subTray)
                    )}
                  </Box>
                ) : (
                  <div className="sub-trays-table_container">
                    <DataTable
                      tableHeaders={subTraysTableHeaderCells}
                      tableRows={subTraysTableBodyCells(trayItem.subItems)}
                      isLoading={false}
                      isErrored={false}
                      disableSorting
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <StickerPrintContainer />
    </div>
  )
}

export default TrayDetails
